import { FilterCombinationPayload, FilterPayload } from "../../../datasources/api-client/data-contracts";
import { FilterItem, GroupFilter, RowFilter } from "../types";

// Convert a single row from the advanced form
// into the API filter payload format
//
// Example:
// {
//   filter: "status",
//   operator: "eq",
//   value: ["active"]
// }
//
// becomes
//
// {
//   id: "status",
//   operator: "eq",
//   value: ["active"]
// }
const mapRow = (row: RowFilter): FilterPayload => {
	return {
		id: row.filter ?? "",
		operator: row.operator as FilterPayload["operator"],
		value: row.value as FilterPayload["value"],
	};
};

// Recursively convert a group structure
// into a nested FilterCombinationPayload
//
// A group can contain:
// - rows
// - nested groups
//
// The recursion allows building deeply nested
// advanced queries like:
const mapGroup = (group: GroupFilter): FilterCombinationPayload => {
	return {
		// Logical operator used inside the group
		operator: group.rowOperator,

		// Convert every child item
		filters: group.rows.map((item) => {
			// Simple filter row
			if (item.type === "row") {
				return mapRow(item.value);
			}

			// Nested group
			return mapGroup(item.value);
		}),
	};
};

// Main mapper used to transform the advanced form state
// into the final query payload sent to the backend
//
// The advanced form always starts from a root group.
// If no valid root group exists, we return
// an empty AND query to avoid crashes.
export const advancedFormToQuery = (items: FilterItem[]): FilterCombinationPayload => {
	// Find the root group
	const root = items.find((item) => item.type === "group");

	// Safe fallback
	if (!root || root.type !== "group") {
		return {
			operator: "and",
			filters: [],
		};
	}

	// Convert the full tree recursively
	return mapGroup(root.value);
};
